import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Progress,
  Spinner,
} from "@learn-me/ui";
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronRight,
  BookOpen,
  Lightbulb,
  FileText,
  Award,
  RotateCcw,
  Send,
} from "lucide-react";
import toast from "react-hot-toast";
import { learningApi } from "@/features/learning/api/learningApi";

type Section = "CONCEPTUAL" | "APPLICATION" | "INTEGRATIVE_CASE";

const SECTION_LABELS: Record<Section, string> = {
  CONCEPTUAL: "Conceptual",
  APPLICATION: "Aplicaci\u00f3n pr\u00e1ctica",
  INTEGRATIVE_CASE: "Caso integrador",
};

const SECTION_ICONS: Record<Section, typeof BookOpen> = {
  CONCEPTUAL: BookOpen,
  APPLICATION: Lightbulb,
  INTEGRATIVE_CASE: FileText,
};

interface ExamInfo {
  id: string;
  courseId: string;
  title: string;
  description: string | null;
  passingScore: number;
  maxAttempts: number;
  timeLimit: number | null;
  totalQuestions: number;
  totalPoints: number;
  conceptualWeight: number;
  applicationWeight: number;
  integrativeCaseWeight: number;
  sectionQuestionCounts: {
    conceptual: number;
    application: number;
    integrativeCase: number;
  };
  attemptsUsed: number;
  canAttempt: boolean;
  bestScore: number | null;
  hasPassed: boolean;
}

interface ActiveQuestion {
  id: string;
  text: string;
  position: number;
  points: number;
  section: Section;
  options: { id: string; text: string; position: number }[];
}

interface ActiveExam {
  attemptId: string;
  finalExamId: string;
  title: string;
  timeLimit: number | null;
  startedAt: string;
  savedAnswers?: Record<string, string> | null;
  questions: ActiveQuestion[];
}

interface SectionScore {
  percentage: number;
  earned: number;
  total: number;
  weight: number;
}

interface ExamResult {
  attemptId: string;
  score: number;
  totalPoints: number;
  earnedPoints: number;
  passed: boolean;
  passingScore: number;
  timeSpent: number;
  sectionScores: {
    conceptual: SectionScore;
    application: SectionScore;
    integrativeCase: SectionScore;
  };
  questions: Array<{
    id: string;
    text: string;
    explanation: string | null;
    points: number;
    section: string;
    earnedPoints: number;
    selectedOptionId: string;
    correctOptionId: string;
    isCorrect: boolean;
    options: Array<{
      id: string;
      text: string;
      isCorrect: boolean;
      isSelected: boolean;
    }>;
  }>;
}

type PageState = "info" | "exam" | "results";

export default function FinalExamPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>("info");
  const [activeExam, setActiveExam] = useState<ActiveExam | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState<Section>("CONCEPTUAL");
  const [result, setResult] = useState<ExamResult | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch exam info
  const {
    data: examInfo,
    isLoading,
    refetch,
  } = useQuery<ExamInfo>({
    queryKey: ["final-exam-student", courseId],
    queryFn: () => learningApi.getFinalExamForStudent(courseId!),
    enabled: !!courseId,
  });

  // Start attempt (también re-hidrata si ya hay un IN_PROGRESS)
  const startMutation = useMutation({
    mutationFn: (finalExamId: string) =>
      learningApi.startFinalExamAttempt(finalExamId),
    onSuccess: (data: ActiveExam) => {
      setActiveExam(data);
      // Si el backend devuelve respuestas previamente guardadas, se hidratan
      setAnswers(data.savedAnswers ?? {});
      setState("exam");
      // El timer se calcula a partir de un deadline absoluto (ver useEffect),
      // no desde un contador en memoria que puede sufrir drift y stale closures.
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al iniciar el examen");
    },
  });

  // Autosave de respuestas con debounce
  const saveProgressMutation = useMutation({
    mutationFn: ({
      attemptId,
      savedAnswers,
    }: {
      attemptId: string;
      savedAnswers: Record<string, string>;
    }) => learningApi.saveFinalExamProgress(attemptId, savedAnswers),
  });
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (state !== "exam" || !activeExam) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveProgressMutation.mutate(
        { attemptId: activeExam.attemptId, savedAnswers: answers },
        { onSuccess: () => setLastSavedAt(new Date()) },
      );
    }, 1500);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, state, activeExam?.attemptId]);

  // Aviso al cerrar pestaña con examen en curso
  useEffect(() => {
    if (state !== "exam") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [state]);

  // Submit attempt
  const submitMutation = useMutation({
    mutationFn: ({
      attemptId,
      answers: ans,
    }: {
      attemptId: string;
      answers: Array<{ questionId: string; selectedOptionId: string }>;
    }) => learningApi.submitFinalExamAttempt(attemptId, ans),
    onSuccess: (data: ExamResult) => {
      setResult(data);
      setState("results");
      if (timerRef.current) clearInterval(timerRef.current);
      refetch();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al enviar el examen");
    },
  });

  const handleSubmit = useCallback(() => {
    if (!activeExam) return;
    setShowConfirm(false);

    const formattedAnswers = Object.entries(answers).map(
      ([questionId, selectedOptionId]) => ({ questionId, selectedOptionId }),
    );

    submitMutation.mutate({
      attemptId: activeExam.attemptId,
      answers: formattedAnswers,
    });
  }, [activeExam, answers, submitMutation]);

  // Timer drift-free basado en deadline absoluto (startedAt + timeLimit).
  // Usamos una ref para que el setInterval siempre llame al handleSubmit
  // más reciente con las respuestas actualizadas (evita stale closure).
  const handleSubmitRef = useRef(handleSubmit);
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (state !== "exam" || !activeExam?.startedAt || !activeExam.timeLimit) {
      return;
    }
    autoSubmittedRef.current = false;
    const deadline =
      new Date(activeExam.startedAt).getTime() +
      activeExam.timeLimit * 60 * 1000;

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setTimeRemaining(remaining);
      if (remaining === 0 && !autoSubmittedRef.current) {
        autoSubmittedRef.current = true;
        handleSubmitRef.current();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    timerRef.current = id;
    return () => clearInterval(id);
  }, [state, activeExam]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!examInfo) {
    return (
      <div className="mx-auto max-w-2xl py-20 text-center">
        <p className="text-muted-foreground">
          Este curso no tiene examen final configurado.
        </p>
      </div>
    );
  }

  // ===== STATE: INFO =====
  if (state === "info") {
    return (
      <div className="mx-auto max-w-3xl space-y-6 p-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold">{examInfo.title}</h1>
          {examInfo.description && (
            <p className="mt-2 text-muted-foreground">{examInfo.description}</p>
          )}
        </div>

        {/* Status */}
        {examInfo.hasPassed && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
            <CheckCircle className="mx-auto h-8 w-8 text-green-600" />
            <p className="mt-2 font-semibold text-green-800">
              Examen aprobado con {examInfo.bestScore}%
            </p>
          </div>
        )}

        {!examInfo.canAttempt && !examInfo.hasPassed && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
            <XCircle className="mx-auto h-8 w-8 text-red-600" />
            <p className="mt-2 font-semibold text-red-800">
              Has agotado los {examInfo.maxAttempts} intentos sin aprobar. No es
              posible obtener el certificado.
            </p>
          </div>
        )}

        {/* Exam Details */}
        <Card>
          <CardContent className="grid gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="text-center">
              <p className="text-2xl font-bold">{examInfo.totalQuestions}</p>
              <p className="text-sm text-muted-foreground">Preguntas</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{examInfo.passingScore}%</p>
              <p className="text-sm text-muted-foreground">
                Puntaje m\u00ednimo
              </p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">
                {examInfo.timeLimit
                  ? `${examInfo.timeLimit} min`
                  : "Sin l\u00edmite"}
              </p>
              <p className="text-sm text-muted-foreground">Tiempo</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">
                {examInfo.attemptsUsed}/{examInfo.maxAttempts}
              </p>
              <p className="text-sm text-muted-foreground">Intentos usados</p>
            </div>
          </CardContent>
        </Card>

        {/* Sections */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Estructura del examen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(
              [
                {
                  key: "CONCEPTUAL" as Section,
                  count: examInfo.sectionQuestionCounts.conceptual,
                  weight: examInfo.conceptualWeight,
                },
                {
                  key: "APPLICATION" as Section,
                  count: examInfo.sectionQuestionCounts.application,
                  weight: examInfo.applicationWeight,
                },
                {
                  key: "INTEGRATIVE_CASE" as Section,
                  count: examInfo.sectionQuestionCounts.integrativeCase,
                  weight: examInfo.integrativeCaseWeight,
                },
              ] as const
            ).map(({ key, count, weight }) => {
              const Icon = SECTION_ICONS[key];
              return (
                <div
                  key={key}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{SECTION_LABELS[key]}</p>
                      <p className="text-sm text-muted-foreground">
                        {count} preguntas
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary">{weight}%</Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Start Button */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={() => startMutation.mutate(examInfo.id)}
            disabled={
              !examInfo.canAttempt ||
              examInfo.hasPassed ||
              startMutation.isPending
            }
          >
            {startMutation.isPending ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                Iniciando...
              </>
            ) : (
              <>
                <ChevronRight className="mr-1 h-5 w-5" />
                {examInfo.attemptsUsed === 0
                  ? "Iniciar examen"
                  : `Iniciar intento ${examInfo.attemptsUsed + 1} de ${examInfo.maxAttempts}`}
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // ===== STATE: EXAM =====
  if (state === "exam" && activeExam) {
    const sectionQuestions = activeExam.questions.filter(
      (q) => q.section === activeSection,
    );
    const answeredInSection = sectionQuestions.filter(
      (q) => answers[q.id],
    ).length;
    const totalAnswered = Object.keys(answers).length;

    return (
      <div className="mx-auto max-w-4xl space-y-4 p-4">
        {/* Timer + Progress Bar */}
        <div className="sticky top-0 z-10 rounded-lg border bg-card p-3 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">{activeExam.title}</h2>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                {totalAnswered}/{activeExam.questions.length} respondidas
              </span>
              {lastSavedAt && (
                <span
                  className="text-xs text-muted-foreground"
                  title={lastSavedAt.toLocaleTimeString()}
                  aria-live="polite"
                >
                  {saveProgressMutation.isPending ? "Guardando…" : "Guardado"}
                </span>
              )}
              {timeRemaining !== null && (
                <div
                  className={`flex items-center gap-1 rounded-lg px-3 py-1 font-mono text-sm font-bold ${
                    timeRemaining < 300
                      ? "bg-red-100 text-red-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  <Clock className="h-4 w-4" />
                  {formatTime(timeRemaining)}
                </div>
              )}
            </div>
          </div>
          <Progress
            value={(totalAnswered / activeExam.questions.length) * 100}
            className="mt-2"
          />
        </div>

        {/* Section Tabs */}
        <div className="flex gap-2">
          {(["CONCEPTUAL", "APPLICATION", "INTEGRATIVE_CASE"] as Section[]).map(
            (section) => {
              const qs = activeExam.questions.filter(
                (q) => q.section === section,
              );
              const answered = qs.filter((q) => answers[q.id]).length;
              const isActive = activeSection === section;
              const Icon = SECTION_ICONS[section];

              return (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm transition-colors ${
                    isActive
                      ? "border-primary bg-primary/5 font-medium text-primary"
                      : "border-gray-200 text-muted-foreground hover:bg-gray-50"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {SECTION_LABELS[section]}
                  <Badge
                    variant={answered === qs.length ? "default" : "secondary"}
                    className="ml-1"
                  >
                    {answered}/{qs.length}
                  </Badge>
                </button>
              );
            },
          )}
        </div>

        {/* Questions */}
        <div className="space-y-4">
          {sectionQuestions.map((question, qIdx) => (
            <Card
              key={question.id}
              className={answers[question.id] ? "border-green-200" : ""}
            >
              <CardContent className="p-5">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">
                    Pregunta {qIdx + 1} de {sectionQuestions.length}
                  </span>
                  <Badge variant="outline">{question.points} pts</Badge>
                </div>
                <p className="mb-4 text-base font-medium">{question.text}</p>
                <div className="space-y-2">
                  {question.options.map((option) => (
                    <label
                      key={option.id}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
                        answers[question.id] === option.id
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50"
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${question.id}`}
                        checked={answers[question.id] === option.id}
                        onChange={() =>
                          setAnswers((prev) => ({
                            ...prev,
                            [question.id]: option.id,
                          }))
                        }
                        className="h-4 w-4"
                      />
                      <span className="text-sm">{option.text}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-4">
          {showConfirm ? (
            <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <p className="text-sm text-amber-800">
                \u00bfEst\u00e1s seguro? No podr\u00e1s cambiar tus respuestas.
                {totalAnswered < activeExam.questions.length && (
                  <strong>
                    {" "}
                    Tienes {activeExam.questions.length - totalAnswered}{" "}
                    preguntas sin responder.
                  </strong>
                )}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowConfirm(false)}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={submitMutation.isPending}
              >
                {submitMutation.isPending
                  ? "Enviando..."
                  : "Confirmar env\u00edo"}
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setShowConfirm(true)}
              disabled={submitMutation.isPending}
            >
              <Send className="mr-1 h-4 w-4" />
              Enviar examen
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ===== STATE: RESULTS =====
  if (state === "results" && result) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 p-6">
        {/* Score Header */}
        <div
          className={`rounded-xl p-8 text-center ${
            result.passed
              ? "bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200"
              : "bg-gradient-to-br from-red-50 to-orange-50 border border-red-200"
          }`}
        >
          <div
            className={`mx-auto mb-3 flex h-20 w-20 items-center justify-center rounded-full ${
              result.passed ? "bg-green-100" : "bg-red-100"
            }`}
          >
            {result.passed ? (
              <CheckCircle className="h-10 w-10 text-green-600" />
            ) : (
              <XCircle className="h-10 w-10 text-red-600" />
            )}
          </div>
          <p className="text-5xl font-bold">{result.score}%</p>
          <Badge
            className="mt-2"
            variant={result.passed ? "default" : "destructive"}
          >
            {result.passed ? "APROBADO" : "NO APROBADO"}
          </Badge>
          <p className="mt-2 text-sm text-muted-foreground">
            Puntaje m\u00ednimo: {result.passingScore}% | Tiempo:{" "}
            {Math.floor(result.timeSpent / 60)} min {result.timeSpent % 60} seg
          </p>
        </div>

        {/* Section Scores */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Desglose por secci\u00f3n</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(
              [
                {
                  key: "CONCEPTUAL" as Section,
                  score: result.sectionScores.conceptual,
                },
                {
                  key: "APPLICATION" as Section,
                  score: result.sectionScores.application,
                },
                {
                  key: "INTEGRATIVE_CASE" as Section,
                  score: result.sectionScores.integrativeCase,
                },
              ] as const
            ).map(({ key, score }) => {
              const Icon = SECTION_ICONS[key];
              const weightedContribution = Math.round(
                (score.percentage * score.weight) / 100,
              );
              return (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{SECTION_LABELS[key]}</span>
                      <Badge variant="outline">{score.weight}%</Badge>
                    </div>
                    <div className="text-right">
                      <span className="font-bold">{score.percentage}%</span>
                      <span className="ml-2 text-sm text-muted-foreground">
                        ({score.earned}/{score.total} pts)
                      </span>
                      <span className="ml-2 text-sm text-muted-foreground">
                        \u2192 {weightedContribution}% ponderado
                      </span>
                    </div>
                  </div>
                  <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                      className={`h-full rounded-full transition-all ${
                        score.percentage >= (examInfo?.passingScore ?? 70)
                          ? "bg-green-500"
                          : "bg-red-400"
                      }`}
                      style={{
                        width: `${score.percentage}%`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Question Review */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Revisi\u00f3n de preguntas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(
              ["CONCEPTUAL", "APPLICATION", "INTEGRATIVE_CASE"] as Section[]
            ).map((section) => {
              const sectionQs = result.questions.filter(
                (q) => q.section === section,
              );
              if (sectionQs.length === 0) return null;
              return (
                <div key={section}>
                  <h3 className="mb-3 flex items-center gap-2 font-semibold">
                    {SECTION_LABELS[section]}
                  </h3>
                  <div className="space-y-3">
                    {sectionQs.map((q, idx) => (
                      <div
                        key={q.id}
                        className={`rounded-lg border p-4 ${
                          q.isCorrect
                            ? "border-green-200 bg-green-50/50"
                            : "border-red-200 bg-red-50/50"
                        }`}
                      >
                        <div className="mb-2 flex items-start justify-between">
                          <p className="font-medium">
                            {idx + 1}. {q.text}
                          </p>
                          {q.isCorrect ? (
                            <CheckCircle className="h-5 w-5 shrink-0 text-green-600" />
                          ) : (
                            <XCircle className="h-5 w-5 shrink-0 text-red-600" />
                          )}
                        </div>
                        <div className="space-y-1">
                          {q.options.map((opt) => (
                            <div
                              key={opt.id}
                              className={`flex items-center gap-2 rounded px-2 py-1 text-sm ${
                                opt.isCorrect
                                  ? "font-medium text-green-700"
                                  : opt.isSelected && !opt.isCorrect
                                    ? "text-red-600 line-through"
                                    : "text-muted-foreground"
                              }`}
                            >
                              {opt.isCorrect && (
                                <CheckCircle className="h-3 w-3 text-green-600" />
                              )}
                              {opt.isSelected && !opt.isCorrect && (
                                <XCircle className="h-3 w-3 text-red-500" />
                              )}
                              {!opt.isCorrect && !opt.isSelected && (
                                <span className="h-3 w-3" />
                              )}
                              {opt.text}
                            </div>
                          ))}
                        </div>
                        {q.explanation && (
                          <p className="mt-2 rounded bg-blue-50 p-2 text-sm text-blue-700">
                            {q.explanation}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-center gap-3">
          {result.passed ? (
            <Button
              size="lg"
              onClick={() => navigate(`/courses/${courseId}/certificate`)}
            >
              <Award className="mr-2 h-5 w-5" />
              Solicitar certificado
            </Button>
          ) : examInfo?.canAttempt &&
            examInfo.attemptsUsed < examInfo.maxAttempts ? (
            <Button
              size="lg"
              onClick={() => {
                setState("info");
                setResult(null);
                setActiveExam(null);
                refetch();
              }}
            >
              <RotateCcw className="mr-2 h-5 w-5" />
              Reintentar examen
            </Button>
          ) : (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center text-red-700">
              <AlertTriangle className="mx-auto mb-2 h-6 w-6" />
              <p className="font-medium">
                Has agotado todos los intentos. No es posible obtener el
                certificado.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}
